import { AudioSelectedIcon } from '@components/Icons/AudioSelectedIcon';
import { VideoSelectedIcon } from '@components/Icons/VideoSelectedIcon';
import { MobileDrawer } from '@components/MobileDrawer/MobileDrawer';
import { Z_INDEX } from '@constants/z-index';
import { useIsMobile } from '@lib/hooks/sizing/useIsMobile';
import { formatRelativeTime } from '@lib/utils/relativeTime';
import { getLibraryUrl } from '@lib/utils/url';
import {
  CheckCircleRounded,
  CloseRounded,
  DeleteOutlineRounded,
  RefreshRounded,
} from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import { IconButton, Modal } from '@mui/material';
import { ActionButton } from '@pages/ActionButton';
import { useModalRegistration } from '@stores/ModalStore';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import css from './FailedMediaListModal.module.scss';

/** Up-right arrow glyph used as the trailing icon on action affordances —
 * matches the arrow on `MediaListLink` so the two surfaces read as the
 * same kind of "go there" cue. */
function GoArrow() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M7 17L17 7M9 7h8v8" />
    </svg>
  );
}

/** `unfinished`: an upload that stopped before the backend had the whole file — not an error the
 * user caused or can retry, so it reads amber rather than red. */
export type FailedRowStatus = 'failed' | 'cancelled' | 'deleted' | 'unfinished';

const CLEAR_ALL_CONFIRM_WINDOW_MS = 4000;

export type FailedRow = {
  key: string;
  filename: string;
  /** Per-row "why" line under the filename. Omit for plain rows that don't
   * need a reason (e.g. a list of deleted media). */
  reason?: string;
  /** When set, renders a coloured label on the right with this status. Omit to hide
   * the label entirely (consistent with how the row also hides retry/remove
   * buttons when those handlers are omitted). */
  status?: FailedRowStatus;
  isAudio: boolean;
  thumbnailUrl: string | null;
  /** Omit when the row isn't retryable (e.g. upload-phase rejects in library
   * mode, where re-running them would just hit the same constraint). */
  onRetry?: () => void;
  /** Omit for read-only rows (e.g. a post-hoc list of media that were
   * deleted — there's nothing left to dismiss). */
  onRemove?: () => void;
  /** What `onRemove` does, so the button can say it: `'dismiss'` only drops the row, `'delete'`
   * also deletes the backend media behind it. Defaults to `'dismiss'`. */
  removeAction?: 'dismiss' | 'delete';
  /** When set, renders a "View in library" button that navigates to
   * `/library/<libraryId>?mediaId=<mediaId>` — the library listing then
   * scrolls to and flashes the matched media (see the highlight logic in
   * LibraryContentMediaList / LibraryContentMediaTable). `onNavigate` is
   * fired in the same click so the caller can close the modal + the
   * upload-activity popover before the route transition takes effect. */
  viewInLibrary?: {
    libraryId: string;
    mediaId: string;
    libraryName?: string;
    onNavigate?: () => void;
  };
};

/** Visual variant for the modal — drives the default status-label colour
 * when individual rows don't override, and any future surface-level
 * accents (header tint, etc.). Default is `'error'` to match the
 * original "Failed uploads" use case. */
export type FailedMediaListModalVariant = 'error' | 'success' | 'warning';

export type FailedGroup = {
  key: string;
  /** Display name of the upload batch this group came from. Undefined for the
   * catch-all bucket of items whose batch was 30-day-evicted or never tracked. */
  batchName?: string;
  /** Used to sort groups newest-first and to render a relative-time label
   * ("2 days ago"). Undefined renders as "Older failures". */
  batchTimestamp?: number;
  rows: FailedRow[];
};

export type FailedMediaListModalProps = {
  open: boolean;
  onClose: () => void;
  title: string;
  /** Sub-header line beneath the title (e.g. "3 items need attention"). */
  metaText: string;
  groups: FailedGroup[];
  /** Optional secondary button (e.g. "Retry all"). Hidden when omitted. */
  onRetryAll?: () => void;
  retryAllLabel?: string;
  retryAllDisabled?: boolean;
  /** Optional "Clear all" action. Hidden when omitted — used by read-only
   * surfaces like the notif-bell "More info" that just want to list items. */
  onClearAll?: () => void;
  clearAllLabel?: string;
  clearAllDisabled?: boolean;
  /** Visual variant — colours per-row status labels when the row's
   * `status` is set but its colour isn't specifically `'cancelled'`. Also
   * the default label colour for rows that have a `status` of `'failed'`
   * or `'deleted'`. */
  variant?: FailedMediaListModalVariant;
};

function statusClassFor(
  status: FailedRowStatus | undefined,
  variant: FailedMediaListModalVariant,
): string {
  if (!status) return '';
  if (status === 'cancelled') return css.statusCancelled;
  if (status === 'unfinished') return css.statusWarning;
  // `failed` / `deleted` (and any future success/warning rows) defer to the
  // modal-level variant for their colour, so a "Deleted media" modal renders
  // green labels while "Failed uploads" renders red.
  if (variant === 'success') return css.statusSuccess;
  if (variant === 'warning') return css.statusWarning;
  return css.statusFailed;
}

function Row({ row, variant }: { row: FailedRow; variant: FailedMediaListModalVariant }) {
  return (
    <div className={css.row}>
      {row.thumbnailUrl ? (
        <div className={css.thumb}>
          <img src={row.thumbnailUrl} alt="" className={css.thumbImage} />
        </div>
      ) : (
        <div className={css.thumbIcon}>
          {row.isAudio ? <AudioSelectedIcon /> : <VideoSelectedIcon />}
        </div>
      )}
      <div className={css.rowMeta}>
        <div className={css.rowName} title={row.filename}>
          {row.filename}
        </div>
        {row.reason && <div className={css.rowWhy}>{row.reason}</div>}
      </div>
      {row.status && (
        <span className={`${css.statusLabel} ${statusClassFor(row.status, variant)}`}>
          {row.status}
        </span>
      )}
      {row.viewInLibrary && (
        <Link
          to={getLibraryUrl(row.viewInLibrary.libraryId, row.viewInLibrary.libraryName, {
            highlightMediaId: row.viewInLibrary.mediaId,
          })}
          className={css.viewBtn}
          onClick={() => row.viewInLibrary?.onNavigate?.()}
        >
          <span>View in library</span>
          <GoArrow />
        </Link>
      )}
      {row.onRetry && (
        <button className={css.retryBtn} onClick={row.onRetry}>
          <RefreshRounded />
          Retry
        </button>
      )}
      {row.onRemove &&
        (row.removeAction === 'delete' ? (
          <button
            className={`${css.removeBtn} ${css.removeBtnDanger}`}
            onClick={row.onRemove}
            aria-label="Delete media"
            title="Delete media"
          >
            <DeleteOutlineRounded />
          </button>
        ) : (
          <button
            className={css.removeBtn}
            onClick={row.onRemove}
            aria-label="Remove from list"
            title="Remove from list"
          >
            <CloseRounded />
          </button>
        ))}
    </div>
  );
}

export function FailedMediaListModal({
  open,
  onClose,
  title,
  metaText,
  groups,
  onRetryAll,
  retryAllLabel = 'Retry all',
  retryAllDisabled,
  onClearAll,
  clearAllLabel = 'Clear all failed',
  clearAllDisabled,
  variant = 'error',
}: FailedMediaListModalProps) {
  // Registered here in the shared shell rather than at each host, so all five surfaces that render
  // this modal are covered at once — the library one, the upload-results modal, the notification
  // bell's details view, a rich event card and a batch card's skipped-duplicates link. Four of
  // those were previously invisible to the modal store.
  useModalRegistration('failed-media', open);

  const totalRows = groups.reduce((sum, g) => sum + g.rows.length, 0);
  const showGroupHeaders = groups.length > 1;
  const showFooter = !!onClearAll || !!onRetryAll;
  const isMobile = useIsMobile();

  // Clear all deletes media on the library surface, so the first click only arms it.
  const [clearArmed, setClearArmed] = useState(false);
  useEffect(() => {
    if (!clearArmed) return;
    const timer = setTimeout(() => setClearArmed(false), CLEAR_ALL_CONFIRM_WINDOW_MS);
    return () => clearTimeout(timer);
  }, [clearArmed]);
  useEffect(() => {
    if (!open) setClearArmed(false);
  }, [open]);

  const handleClearAll = () => {
    if (!clearArmed) {
      setClearArmed(true);
      return;
    }
    setClearArmed(false);
    onClearAll?.();
  };

  // Shared inner content — same header / scrolling body / footer for both the
  // desktop modal and the mobile drawer.
  const inner = (
    <>
      <IconButton className={css.closeButton} onClick={onClose} size="small" aria-label="Close">
        <CloseIcon />
      </IconButton>
      <div className={css.header}>
        <h2 className={css.title}>{title}</h2>
        <p className={css.meta}>{metaText}</p>
      </div>

      <div className={css.body}>
        {totalRows === 0 ? (
          <div className={css.empty}>
            <CheckCircleRounded />
            <div className={css.emptyTitle}>Nothing failed</div>
            <div className={css.emptySub}>All your uploads completed successfully</div>
          </div>
        ) : (
          groups.map((group) => (
            <div key={group.key} className={css.group}>
              {showGroupHeaders && (
                <div className={css.groupHeader}>
                  <span className={css.groupTitle}>{group.batchName ?? 'Older failures'}</span>
                  {group.batchTimestamp !== undefined && (
                    <span className={css.groupTime}>
                      {formatRelativeTime(group.batchTimestamp)}
                    </span>
                  )}
                </div>
              )}
              {group.rows.map((row) => (
                <Row key={row.key} row={row} variant={variant} />
              ))}
            </div>
          ))
        )}
      </div>

      {showFooter && (
        <div className={css.footer}>
          {onClearAll && (
            <ActionButton
              variant={clearArmed ? 'danger' : 'secondary'}
              onClick={handleClearAll}
              disabled={clearAllDisabled ?? totalRows === 0}
            >
              {clearArmed ? `Click again to ${clearAllLabel.toLowerCase()}` : clearAllLabel}
            </ActionButton>
          )}
          {onRetryAll && (
            <ActionButton
              variant="primary"
              onClick={onRetryAll}
              disabled={retryAllDisabled ?? totalRows === 0}
            >
              {retryAllLabel}
            </ActionButton>
          )}
        </div>
      )}
    </>
  );

  // Mobile: a bottom drawer stacked ABOVE the notifications/uploads drawer
  // (deliberately unlike desktop, which is a modal over the popover).
  if (isMobile) {
    return (
      <MobileDrawer
        open={open}
        onClose={onClose}
        onOpen={() => {}}
        frosted
        // Stacks above the notifications/uploads drawer (1400), below tooltips (1500).
        zIndex={Z_INDEX.aboveMobileDrawer}
      >
        <div className={css.drawerBody}>{inner}</div>
      </MobileDrawer>
    );
  }

  return (
    <Modal open={open} onClose={onClose} className={css.modalRoot} closeAfterTransition>
      <div className={css.modal} onClick={(e) => e.stopPropagation()}>
        {inner}
      </div>
    </Modal>
  );
}
